import {
  type CreateParams,
  type CreateResult,
  type DataProvider,
  type GetListParams,
  type GetListResult,
  type GetManyParams,
  type GetManyReferenceParams,
  type GetManyReferenceResult,
  type GetManyResult,
  type GetOneParams,
  type GetOneResult,
  HttpError,
  type Identifier,
  type RaRecord,
  type UpdateParams,
  type UpdateResult,
} from 'react-admin'
import { z } from 'zod'

import { isWithinAnyStay, OPERATION_KINDS, PRODUCT_CLASSES, type ScaleEvent, SPECIAL_EVENT_TYPES } from './data'
import {
  MARPOL_ANNEXES,
  parseScaleRecordId,
  type ScaleKey,
  type ScaleRecord,
  type ScalesSource,
  SourceError,
  type SourceRun,
  type SourceRunSource,
  summarise,
  type SyncStart,
  toEventRecord,
} from './data/source'

export interface ScalesDataProvider extends DataProvider {
  startRead: (source: SourceRunSource) => Promise<SyncStart>
  latestRun: (source: SourceRunSource) => Promise<SourceRun | undefined>
  getSourceRun: (id: string) => Promise<SourceRun | undefined>
}

const scaleFilterSchema = z
  .object({
    q: z.string().optional(),
    finished: z.union([z.boolean(), z.enum(['true', 'false']).transform(value => value === 'true')]).optional(),
  })
  .catchall(z.unknown())

const emptyAsNull = <T extends z.ZodType>(schema: T) =>
  z.preprocess(value => (value === undefined || value === '' ? null : value), schema.nullable())

const specialEventFields = z.object({
  type: z.enum(SPECIAL_EVENT_TYPES),
  startTime: z.string(),
  endTime: emptyAsNull(z.string()),
  connectionPoint: emptyAsNull(z.string()),
  productClass: emptyAsNull(z.enum(PRODUCT_CLASSES)),
  product: emptyAsNull(z.string().trim()).transform(value => (value === '' ? null : value)),
  quantityT: emptyAsNull(z.coerce.number().nonnegative()),
  operationKind: emptyAsNull(z.enum(OPERATION_KINDS)),
  marpolAnnex: emptyAsNull(z.enum(MARPOL_ANNEXES)),
  marpolNoticeAt: emptyAsNull(z.string()),
})

const withMarpolAnnex = <T extends z.infer<typeof specialEventFields>>(schema: z.ZodType<T>) =>
  schema.refine(event => event.type !== 'MARPOL' || event.marpolAnnex !== null, {
    path: ['marpolAnnex'],
    message: 'A MARPOL event needs its annex',
  })

const specialEventSchema = withMarpolAnnex(specialEventFields)

const newSpecialEventSchema = withMarpolAnnex(specialEventFields.extend({ scaleRecordId: z.string() }))

const archiveSchema = z.object({ finished: z.boolean() })

const contains = (value: string | null, fragment: string): boolean => value?.toLowerCase().includes(fragment.toLowerCase()) ?? false

const sortText = (value: unknown): string => (typeof value === 'string' ? value : JSON.stringify(value))

const compareBy =
  (field: string, order: string) =>
  (a: object, b: object): number => {
    const left: unknown = Reflect.get(a, field)
    const right: unknown = Reflect.get(b, field)
    if (left === right) return 0
    if (left === null || left === undefined) return 1
    if (right === null || right === undefined) return -1
    const result = sortText(left).localeCompare(sortText(right), undefined, { numeric: true })
    return order === 'DESC' ? -result : result
  }

const toHttpError = (error: unknown): Error => {
  if (error instanceof SourceError) {
    if (error.code === 'SYNC_REFUSED') return new HttpError(error.message, 502)
    const status = error.code === 'NOT_FOUND' ? 404 : 400
    return new HttpError(`resources.scale_events.errors.${error.code.toLowerCase()}`, status)
  }
  return error instanceof Error ? error : new Error(String(error))
}

const parse = <T>(schema: z.ZodType<T>, data: unknown): T => {
  const result = schema.safeParse(data)
  if (!result.success) throw new HttpError(result.error.message, 400)
  return result.data
}

const notFound = (resource: string, id: Identifier) => new HttpError(`${resource} ${String(id)} not found`, 404)
const unsupported = (operation: string, resource: string) => new HttpError(`${operation} is not supported on ${resource}`, 405)

const assertStartsWithinStay = (events: readonly ScaleEvent[], startTime: string): void => {
  if (!events.some(event => event.type === 'PLACE')) throw new SourceError('NO_STAYS')
  if (!isWithinAnyStay(events, Date.parse(startTime))) throw new SourceError('START_OUTSIDE_STAYS')
}

const keyOf = (id: Identifier): ScaleKey => {
  const key = parseScaleRecordId(String(id))
  if (!key) throw notFound('scales', id)
  return key
}

const page = <T>(records: T[], pagination?: { page: number; perPage: number }) => {
  const { page: number, perPage } = pagination ?? { page: 1, perPage: records.length }
  return { data: records.slice((number - 1) * perPage, number * perPage), total: records.length }
}

export const createScalesDataProvider = (source: ScalesSource, now: () => number = Date.now): ScalesDataProvider => {
  const scaleRecord = async (key: ScaleKey): Promise<ScaleRecord> => {
    const found = await source.getScale(key)
    if (!found) throw notFound('scales', `${String(key.year)}-${key.scaleId}`)
    return summarise(found, now())
  }

  const scaleRecords = async (filter: unknown): Promise<ScaleRecord[]> => {
    const { q, finished } = parse(scaleFilterSchema, filter ?? {})
    const at = now()
    return (await source.listScales({ finished }))
      .map(scale => summarise(scale, at))
      .filter(
        scale =>
          !q ||
          contains(scale.scaleId, q) ||
          contains(scale.currentMuelle, q) ||
          contains(scale.vessel, q) ||
          scale.terminals.some(terminal => contains(terminal, q)),
      )
  }

  const eventRecords = async (scaleRecordId: Identifier) => {
    const found = await source.getScale(keyOf(scaleRecordId))
    if (!found) throw notFound('scales', scaleRecordId)
    return found.events.sort((a, b) => Date.parse(b.startTime) - Date.parse(a.startTime) || b.id.localeCompare(a.id)).map(toEventRecord)
  }

  const run = async <T>(fn: () => Promise<T>): Promise<T> => {
    try {
      return await fn()
    } catch (error) {
      throw toHttpError(error)
    }
  }

  function getList<R extends RaRecord>(resource: string, params: GetListParams): Promise<GetListResult<R>>
  function getList(resource: string, params: GetListParams): Promise<GetListResult<RaRecord>> {
    return run(async () => {
      if (resource === 'connection_points') {
        const points = (await source.listConnectionPoints()).map(point => ({ id: point.code, ...point }))
        return { data: points, total: points.length }
      }
      if (resource !== 'scales') throw unsupported('getList', resource)
      const records = await scaleRecords(params.filter)
      const sorted = params.sort ? [...records].sort(compareBy(params.sort.field, params.sort.order)) : records
      return page(sorted, params.pagination)
    })
  }

  function getOne<R extends RaRecord>(resource: string, params: GetOneParams<R>): Promise<GetOneResult<R>>
  function getOne(resource: string, params: GetOneParams<RaRecord>): Promise<GetOneResult<RaRecord>> {
    return run(async () => {
      if (resource === 'scales') return { data: await scaleRecord(keyOf(params.id)) }
      if (resource === 'scale_events') {
        const event = await source.getEvent(String(params.id))
        if (!event) throw notFound(resource, params.id)
        return { data: toEventRecord(event) }
      }
      throw unsupported('getOne', resource)
    })
  }

  function getMany<R extends RaRecord>(resource: string, params: GetManyParams<R>): Promise<GetManyResult<R>>
  function getMany(resource: string, params: GetManyParams<RaRecord>): Promise<GetManyResult<RaRecord>> {
    return run(async () => {
      if (resource === 'scales') {
        const records = await Promise.all(params.ids.map(id => scaleRecord(keyOf(id)).catch(() => undefined)))
        return { data: records.flatMap(record => (record ? [record] : [])) }
      }
      if (resource === 'scale_events') {
        const events = await Promise.all(params.ids.map(id => source.getEvent(String(id))))
        return { data: events.flatMap(event => (event ? [toEventRecord(event)] : [])) }
      }
      throw unsupported('getMany', resource)
    })
  }

  function getManyReference<R extends RaRecord>(resource: string, params: GetManyReferenceParams): Promise<GetManyReferenceResult<R>>
  function getManyReference(resource: string, params: GetManyReferenceParams): Promise<GetManyReferenceResult<RaRecord>> {
    return run(async () => {
      if (resource !== 'scale_events' || params.target !== 'scaleRecordId') throw unsupported('getManyReference', resource)
      const records = await eventRecords(params.id)
      const sorted =
        params.sort.field === 'startTime' && params.sort.order === 'DESC'
          ? records
          : [...records].sort(compareBy(params.sort.field, params.sort.order))
      return page(sorted, params.pagination)
    })
  }

  function create<R extends RaRecord>(resource: string, params: CreateParams): Promise<CreateResult<R>>
  function create(resource: string, params: CreateParams): Promise<CreateResult<RaRecord>> {
    return run(async () => {
      if (resource === 'scale_events') {
        const { scaleRecordId, ...input } = parse(newSpecialEventSchema, params.data)
        const key = keyOf(scaleRecordId)
        if ((await scaleRecord(key)).closed) throw new SourceError('SCALE_CLOSED')
        assertStartsWithinStay((await source.getScale(key))?.events ?? [], input.startTime)
        return { data: toEventRecord(await source.createSpecialEvent(key, input)) }
      }
      throw unsupported('create', resource)
    })
  }

  function update<R extends RaRecord>(resource: string, params: UpdateParams<R>): Promise<UpdateResult<R>>
  function update(resource: string, params: UpdateParams<RaRecord>): Promise<UpdateResult<RaRecord>> {
    return run(async () => {
      if (resource === 'scales') {
        const { finished } = parse(archiveSchema, params.data)
        const updated = await source.setFinished(keyOf(params.id), finished)
        return { data: await scaleRecord(updated) }
      }
      if (resource === 'scale_events') {
        const existing = await source.getEvent(String(params.id))
        if (existing?.type === 'PLACE') throw new SourceError('NOT_SPECIAL')
        const input = parse(specialEventSchema, { ...params.previousData, ...params.data })
        if (existing && existing.type !== input.type) throw new SourceError('NOT_SPECIAL')
        if (existing && Date.parse(existing.startTime) !== Date.parse(input.startTime)) {
          const scale = await source.getScale({ year: existing.year, scaleId: existing.scaleId })
          assertStartsWithinStay(scale?.events ?? [], input.startTime)
        }
        return { data: toEventRecord(await source.updateSpecialEvent(String(params.id), input)) }
      }
      throw unsupported('update', resource)
    })
  }

  return {
    getList,
    getOne,
    getMany,
    getManyReference,
    create,
    update,
    updateMany: (resource: string) => Promise.reject(unsupported('updateMany', resource)),
    delete: (resource: string) => Promise.reject(unsupported('delete', resource)),
    deleteMany: (resource: string) => Promise.reject(unsupported('deleteMany', resource)),

    startRead: from => run(() => source.startRead(from)),
    latestRun: from => run(() => source.latestRun(from)),
    getSourceRun: id => run(() => source.getSourceRun(id)),
  }
}
